import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { riskToneOf } from 'components/ScreeningComposer/riskTone';
import { normalizeExternalUrl } from 'utils/common';
import { RiskAgent, RiskLevel } from './entityRiskEvaluation';
import {
  EntityRiskReport,
  RiskReportFinding,
  RiskReportSource,
} from './entityRiskReport';
import { TreeWindowDialog } from 'components/Common/treeWindowDialog';

type SeverityFilter = 'all' | 'medium' | 'high';

type Highlight =
  | { kind: 'check'; id: RiskAgent }
  | { kind: 'finding'; id: string }
  | { kind: 'source'; id: string }
  | null;

interface Edge {
  from: string;
  to: string;
  tone: string;
  findingId?: string;
  sourceUrl?: string;
  agent: RiskAgent;
}

/** Findings shown before "Show all", so a large run stays readable. */
const FINDINGS_CAP = 12;

const SEVERITY_FILTERS: Array<{ key: SeverityFilter; label: string }> = [
  { key: 'all', label: 'All findings' },
  { key: 'medium', label: 'Medium and high' },
  { key: 'high', label: 'High only' },
];

const passesSeverity = (
  severity: RiskLevel | null,
  filter: SeverityFilter,
): boolean =>
  filter === 'all' ||
  severity === 'High' ||
  (filter === 'medium' && severity === 'Medium');

const findingsCopy = (count: number) =>
  `${count} ${count === 1 ? 'finding' : 'findings'}`;

const confidenceCopy = (confidence: number | null) =>
  confidence === null ? '' : `${Math.round(confidence * 100)}% confidence`;

interface EntityHubRiskNetworkProps {
  report: EntityRiskReport;
  dialog: TreeWindowDialog;
}

/**
 * Network intelligence for Export and Transshipment Risk, opened as a window
 * over the report the way the Ownership tree does: the entity, the checks
 * that ran on it, what each check found and the sources behind each finding.
 * Hovering a node lights the path through it. Selecting a finding reads it
 * in the inspector.
 */
const EntityHubRiskNetwork: React.FC<EntityHubRiskNetworkProps> = ({
  report,
  dialog,
}) => {
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [focusAgent, setFocusAgent] = useState<RiskAgent | null>(null);
  const [highlight, setHighlight] = useState<Highlight>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [paths, setPaths] = useState<Array<Edge & { d: string; key: string }>>(
    [],
  );
  const canvasRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef(new Map<string, HTMLElement>());

  const register = useCallback(
    (key: string) => (element: HTMLElement | null) => {
      if (element) nodeRefs.current.set(key, element);
      else nodeRefs.current.delete(key);
    },
    [],
  );

  const filtered = useMemo(
    () =>
      report.findings.filter(
        (finding) =>
          passesSeverity(finding.severity, severity) &&
          (!focusAgent || finding.agent === focusAgent),
      ),
    [focusAgent, report.findings, severity],
  );
  // Memoized: the links redraw whenever this list changes identity.
  const visibleFindings = useMemo(
    () => (showAll ? filtered : filtered.slice(0, FINDINGS_CAP)),
    [filtered, showAll],
  );
  const visibleIds = useMemo(
    () => new Set(visibleFindings.map((finding) => finding.id)),
    [visibleFindings],
  );
  const visibleSources = useMemo(
    () =>
      report.sources.filter((source) =>
        source.findingIds.some((id) => visibleIds.has(id)),
      ),
    [report.sources, visibleIds],
  );
  const findingById = useMemo(
    () =>
      new Map<string, RiskReportFinding>(
        report.findings.map((finding) => [finding.id, finding]),
      ),
    [report.findings],
  );
  const sourceOfFinding = useMemo(() => {
    const map = new Map<string, RiskReportSource>();
    report.sources.forEach((source) =>
      source.findingIds.forEach((id) => map.set(id, source)),
    );
    return map;
  }, [report.sources]);

  const edges: Edge[] = useMemo(() => {
    const list: Edge[] = report.checks.map((check) => ({
      from: 'subject',
      to: `check:${check.agent}`,
      tone: check.status === 'answered' ? riskToneOf(check.risk) : 'none',
      agent: check.agent,
    }));
    visibleFindings.forEach((finding) => {
      const tone = riskToneOf(finding.severity);
      list.push({
        from: `check:${finding.agent}`,
        to: `finding:${finding.id}`,
        tone: tone,
        findingId: finding.id,
        agent: finding.agent,
      });
      const source = sourceOfFinding.get(finding.id);
      if (source) {
        list.push({
          from: `finding:${finding.id}`,
          to: `source:${source.url}`,
          tone: tone,
          findingId: finding.id,
          sourceUrl: source.url,
          agent: finding.agent,
        });
      }
    });
    return list;
  }, [report.checks, sourceOfFinding, visibleFindings]);

  // What a hover lights up: every node and link on a path through it.
  const lit = useMemo(() => {
    const nodes = new Set<string>();
    if (!highlight) return nodes;
    const addFinding = (finding: RiskReportFinding | undefined) => {
      if (!finding) return;
      nodes.add(`finding:${finding.id}`);
      nodes.add(`check:${finding.agent}`);
      const source = sourceOfFinding.get(finding.id);
      if (source) nodes.add(`source:${source.url}`);
    };
    nodes.add('subject');
    if (highlight.kind === 'finding') {
      addFinding(findingById.get(highlight.id));
    } else if (highlight.kind === 'check') {
      nodes.add(`check:${highlight.id}`);
      visibleFindings
        .filter((finding) => finding.agent === highlight.id)
        .forEach(addFinding);
    } else {
      const source = report.sources.find((item) => item.url === highlight.id);
      source?.findingIds
        .filter((id) => visibleIds.has(id))
        .forEach((id) => addFinding(findingById.get(id)));
    }
    return nodes;
  }, [
    findingById,
    highlight,
    report.sources,
    sourceOfFinding,
    visibleFindings,
    visibleIds,
  ]);

  const nodeState = (key: string) =>
    !highlight ? '' : lit.has(key) ? ' is-lit' : ' is-dim';

  // The links are drawn between the rendered cards, so they follow the layout
  // as it wraps, filters and grows.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const draw = () => {
      const box = canvas.getBoundingClientRect();
      const next: Array<Edge & { d: string; key: string }> = [];
      edges.forEach((edge) => {
        const from = nodeRefs.current.get(edge.from);
        const to = nodeRefs.current.get(edge.to);
        if (!from || !to) return;
        const a = from.getBoundingClientRect();
        const b = to.getBoundingClientRect();
        const x1 = a.right - box.left;
        const y1 = a.top + a.height / 2 - box.top;
        const x2 = b.left - box.left;
        const y2 = b.top + b.height / 2 - box.top;
        if (x2 <= x1) return;
        const bend = (x2 - x1) / 2;
        next.push({
          ...edge,
          key: `${edge.from}>${edge.to}`,
          d: `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${
            x2 - bend
          } ${y2}, ${x2} ${y2}`,
        });
      });
      setPaths(next);
    };
    draw();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [edges]);

  // A finding the filters now hide is no longer shown as selected.
  const selected =
    selectedId && visibleIds.has(selectedId)
      ? findingById.get(selectedId) || null
      : null;
  const selectedSource = selected ? sourceOfFinding.get(selected.id) : null;
  const selectedUrl = normalizeExternalUrl(selected?.url || '');
  const overallTone = riskToneOf(report.overallRisk);
  const hiddenCount = filtered.length - visibleFindings.length;

  const focusedCheck = focusAgent
    ? report.checks.find((check) => check.agent === focusAgent)
    : null;

  return (
    <div className='bo-tree-shell is-dialog entity-hub-network-dialog'>
      <div className='bo-tree-bar'>
        <span className='bo-tree-dialog-icon' aria-hidden='true'>
          <HubOutlinedIcon sx={{ fontSize: 19 }} />
        </span>
        <div className='bo-tree-dialog-title'>
          <h2 id={dialog.titleId}>Network intelligence</h2>
          {dialog.meta ? <p className='tnum'>{dialog.meta}</p> : null}
        </div>
        <div
          className='entity-hub-network-filters'
          role='group'
          aria-label='Filter findings by severity'
        >
          {SEVERITY_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type='button'
              className={`entity-hub-network-chip${
                severity === filter.key ? ' is-on' : ''
              }`}
              aria-pressed={severity === filter.key}
              onClick={() => {
                setSeverity(filter.key);
                setShowAll(false);
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>
        {focusedCheck ? (
          <button
            type='button'
            className='bo-tree-btn'
            onClick={() => setFocusAgent(null)}
          >
            Show every check
          </button>
        ) : null}
        {dialog.action}
        <span className='bo-tree-bar-rule' aria-hidden='true' />
        <button
          type='button'
          className='bo-tree-close'
          aria-label='Close network intelligence'
          onClick={dialog.onClose}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </button>
      </div>

      <div className='bo-tree-body'>
        <div className='bo-tree-stage entity-hub-network-stage'>
          <div
            ref={canvasRef}
            className={`entity-hub-network-canvas${
              highlight ? ' has-lit' : ''
            }`}
            onMouseLeave={() => setHighlight(null)}
          >
            <svg className='entity-hub-network-links' aria-hidden='true'>
              {paths.map((path) => {
                const isLit =
                  highlight && lit.has(path.from) && lit.has(path.to);
                return (
                  <path
                    key={path.key}
                    d={path.d}
                    className={`tone-${path.tone}${
                      highlight ? (isLit ? ' is-lit' : ' is-dim') : ''
                    }`}
                  />
                );
              })}
            </svg>

            <div className='entity-hub-network-col is-subject'>
              <span className='entity-hub-network-col-label'>Entity</span>
              <div
                ref={register('subject')}
                className={`entity-hub-network-node is-subject tone-${overallTone}${nodeState(
                  'subject',
                )}`}
              >
                <ApartmentOutlinedIcon aria-hidden='true' />
                <strong>{report.entityName || 'This entity'}</strong>
                {report.country ? <span>{report.country}</span> : null}
                <em className={`tone-${overallTone}`}>
                  {report.overallRisk
                    ? `${report.overallRisk} risk`
                    : 'No verdict'}
                </em>
              </div>
            </div>

            <div className='entity-hub-network-col'>
              <span className='entity-hub-network-col-label'>
                Checks · {report.counts.answered} of {report.counts.checks}{' '}
                answered
              </span>
              {report.checks.map((check) => {
                const key = `check:${check.agent}`;
                const tone =
                  check.status === 'answered' ? riskToneOf(check.risk) : 'none';
                return (
                  <button
                    key={check.agent}
                    ref={register(key)}
                    type='button'
                    className={`entity-hub-network-node is-check tone-${tone}${nodeState(
                      key,
                    )}${focusAgent === check.agent ? ' is-focus' : ''}`}
                    aria-pressed={focusAgent === check.agent}
                    onMouseEnter={() =>
                      setHighlight({ kind: 'check', id: check.agent })
                    }
                    onFocus={() =>
                      setHighlight({ kind: 'check', id: check.agent })
                    }
                    onClick={() => {
                      setFocusAgent((current) =>
                        current === check.agent ? null : check.agent,
                      );
                      setShowAll(false);
                    }}
                  >
                    <strong>{check.label}</strong>
                    <span>{check.note}</span>
                    <em className={`tone-${tone}`}>
                      {check.status === 'answered'
                        ? `${check.risk || 'No verdict'} · ${findingsCopy(
                            check.findingIds.length,
                          )}`
                        : check.status === 'failed'
                        ? 'Did not answer'
                        : 'Not run'}
                    </em>
                  </button>
                );
              })}
            </div>

            <div className='entity-hub-network-col is-findings'>
              <span className='entity-hub-network-col-label'>
                Findings · {filtered.length}
              </span>
              {visibleFindings.length ? (
                visibleFindings.map((finding) => {
                  const key = `finding:${finding.id}`;
                  const tone = riskToneOf(finding.severity);
                  return (
                    <button
                      key={finding.id}
                      ref={register(key)}
                      type='button'
                      className={`entity-hub-network-node is-finding tone-${tone}${nodeState(
                        key,
                      )}${selectedId === finding.id ? ' is-selected' : ''}`}
                      aria-pressed={selectedId === finding.id}
                      onMouseEnter={() =>
                        setHighlight({ kind: 'finding', id: finding.id })
                      }
                      onFocus={() =>
                        setHighlight({ kind: 'finding', id: finding.id })
                      }
                      onClick={() =>
                        setSelectedId((current) =>
                          current === finding.id ? null : finding.id,
                        )
                      }
                    >
                      <span className='entity-hub-network-finding-meta'>
                        <em className={`tone-${tone}`}>
                          {finding.severity || 'Not scored'}
                        </em>
                        {finding.agentLabel}
                      </span>
                      <strong>{finding.claim}</strong>
                    </button>
                  );
                })
              ) : (
                <p className='entity-hub-network-empty'>
                  {report.findings.length
                    ? 'No findings match this filter.'
                    : 'No findings to map: every check that answered came back clean.'}
                </p>
              )}
              {hiddenCount > 0 ? (
                <button
                  type='button'
                  className='entity-hub-network-more'
                  onClick={() => setShowAll(true)}
                >
                  Show {hiddenCount} more
                </button>
              ) : null}
            </div>

            <div className='entity-hub-network-col is-sources'>
              <span className='entity-hub-network-col-label'>
                Sources · {visibleSources.length}
              </span>
              {visibleSources.map((source) => {
                const key = `source:${source.url}`;
                const cited = source.findingIds.filter((id) =>
                  visibleIds.has(id),
                ).length;
                return (
                  <div
                    key={source.url}
                    ref={register(key)}
                    className={`entity-hub-network-node is-source${nodeState(
                      key,
                    )}`}
                    onMouseEnter={() =>
                      setHighlight({ kind: 'source', id: source.url })
                    }
                  >
                    <LinkOutlinedIcon aria-hidden='true' />
                    <strong title={source.url}>{source.title}</strong>
                    <span>
                      {source.domain && source.domain !== source.title
                        ? `${source.domain} · `
                        : ''}
                      cited by {findingsCopy(cited)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <aside
          className='bo-tree-inspector'
          aria-label='Finding'
          aria-live='polite'
        >
          {selected ? (
            <>
              <div className='bo-tree-inspector-scroll'>
                <div className='bo-tree-inspector-head'>
                  <div>
                    <h3>{selected.claim}</h3>
                    <div className='bo-tree-tags'>
                      <span className={`tone-${riskToneOf(selected.severity)}`}>
                        {selected.severity || 'Not scored'}
                      </span>
                      <span>{selected.agentLabel}</span>
                      {selected.category ? (
                        <span>{selected.category}</span>
                      ) : null}
                      {selected.date ? <span>{selected.date}</span> : null}
                      {selected.confidence !== null ? (
                        <span>{confidenceCopy(selected.confidence)}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <h4>Source</h4>
                {selectedUrl ? (
                  <a
                    className='bo-tree-source'
                    href={selectedUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {selectedSource?.title ||
                      selected.sourceName ||
                      'View source'}
                    <OpenInNewIcon aria-hidden='true' />
                  </a>
                ) : (
                  <p>No source link was saved with this finding.</p>
                )}
              </div>
            </>
          ) : (
            <div className='bo-tree-inspector-empty'>
              <p className='lead'>Pick a finding</p>
              <p>
                Its source shows here. Hover a check, finding or source to trace
                its connections, or select a check to show only its findings.
              </p>
            </div>
          )}
        </aside>
      </div>

      <div className='bo-tree-legend' aria-label='Severity'>
        <span>
          <i className='swatch high' aria-hidden='true' />
          High
        </span>
        <span>
          <i className='swatch medium' aria-hidden='true' />
          Medium
        </span>
        <span>
          <i className='swatch low' aria-hidden='true' />
          Low
        </span>
        <span>
          <i className='swatch none' aria-hidden='true' />
          Not scored
        </span>
        <span className='hint'>Hover a node to trace it · Esc closes</span>
      </div>
    </div>
  );
};

export default EntityHubRiskNetwork;
